import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';
import apiErrorMessage from '../utils/apiErrorMessage';
import { isJsonPayload } from '../../../shared/api/payload';

const TOAST_OPTIONS = {
  position: toast.POSITION.TOP_CENTER,
  draggable: false,
  closeOnClick: false,
  autoClose: 5000,
  hideProgressBar: true,
};

function handleApiError(error) {
  toast.error(apiErrorMessage(error), TOAST_OPTIONS);
}

export default function useV3Offers() {
  const { csrfToken, team, dispatch } = useV3OfferContext();

  const setHeaders = useCallback(() => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
  }, [csrfToken]);

  const fetchOffer = useCallback(async (offerId) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_LOADING, payload: true });
    try {
      const response = await axios.get(`/teams/${team.id}/offers/v3/${offerId}`);
      dispatch({ type: V3_ACTIONS.SET_OFFER, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_LOADING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const createOffer = useCallback(async (offerParams) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.post(`/teams/${team.id}/offers/v3`, {
        v3_offer: offerParams,
      });
      dispatch({ type: V3_ACTIONS.SET_OFFER, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  // `syncContext: false` PATCHes without replacing the context offer. Used to
  // flush an edit belonging to an offer the booker has already navigated away
  // from: SET_OFFER is a wholesale replace, so echoing that response would
  // repaint the editor, the split card and the pool controls on the PREVIOUS
  // artist until the current one's own save happens to answer.
  const updateOffer = useCallback(async (offerId, offerParams, { syncContext = true } = {}) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.patch(`/teams/${team.id}/offers/v3/${offerId}`, {
        v3_offer: offerParams,
      });
      if (syncContext) dispatch({ type: V3_ACTIONS.SET_OFFER, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const deleteOffer = useCallback(async (offerId) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.delete(`/teams/${team.id}/offers/v3/${offerId}.json`);
      if (!isJsonPayload(response.data)) {
        toast.error("Couldn't delete this offer. Please reload the page and try again.", TOAST_OPTIONS);
        return;
      }
      dispatch({ type: V3_ACTIONS.SET_OFFER, payload: null });
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const acceptOffer = useCallback(async (offerId) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.post(`/teams/${team.id}/offers/v3/${offerId}/accept.json`);
      if (!isJsonPayload(response.data)) {
        toast.error("Couldn't accept this offer. Please reload the page and try again.", TOAST_OPTIONS);
        return undefined;
      }
      dispatch({ type: V3_ACTIONS.SET_OFFER, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const declineOffer = useCallback(async (offerId) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.post(`/teams/${team.id}/offers/v3/${offerId}/decline.json`);
      if (!isJsonPayload(response.data)) {
        toast.error("Couldn't decline this offer. Please reload the page and try again.", TOAST_OPTIONS);
        return undefined;
      }
      dispatch({ type: V3_ACTIONS.SET_OFFER, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const sendOffer = useCallback(async (offerId, offerParams) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.post(`/teams/${team.id}/offers/v3/${offerId}/send_offer.json`, {
        v3_offer: offerParams,
      });
      if (!isJsonPayload(response.data)) {
        toast.error("Couldn't send this offer. Please reload the page and try again.", TOAST_OPTIONS);
        return undefined;
      }
      dispatch({ type: V3_ACTIONS.SET_OFFER, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  return {
    fetchOffer,
    createOffer,
    updateOffer,
    deleteOffer,
    acceptOffer,
    declineOffer,
    sendOffer,
  };
}
